/**
 * Auth service — endpoints de autenticação JWT do iTap Orchestrator (§6).
 *
 * Endpoints cobertos:
 *   - POST /api/v1/auth/login    → autentica e devolve TokenResponse
 *   - POST /api/v1/auth/refresh  → roda o refresh_token e devolve novo par
 *   - POST /api/v1/auth/logout   → revoga o refresh_token (204, idempotente)
 *   - GET  /api/v1/me            → dados do usuário autenticado + tenants
 *
 * Detalhes importantes:
 *   - `login` e `refresh` NÃO precisam de Authorization (o interceptor da
 *     instância `api` injeta Bearer apenas quando há `accessToken` no store;
 *     antes do login não há, então não vai header — comportamento correto).
 *   - `refresh` usa o `body.refresh_token`, não o header.
 *   - Os erros de login (401/422) são tratados localmente pela página de
 *     login — passamos `_skipErrorToast: true` para que o response interceptor
 *     não dispare toast global de "sessão expirada" enquanto o usuário ainda
 *     está tentando entrar.
 *   - `logout` é idempotente no backend: chamar com um refresh_token já
 *     revogado retorna 204 do mesmo jeito.
 *   - `getMe` requer Bearer — o interceptor injeta automaticamente.
 */

import axios from 'axios'

import { api } from '@/services/api'
import { config } from '@/lib/config'
import { API_TIMEOUT_MS } from '@/lib/constants'
import type { MeResponse, TokenResponse } from '@/types/api'

// ---------------------------------------------------------------------------
// Endpoints (path relativo — baseURL = config.apiUrl)
// ---------------------------------------------------------------------------

const ENDPOINTS = {
  login: '/api/v1/auth/login',
  refresh: '/api/v1/auth/refresh',
  logout: '/api/v1/auth/logout',
  me: '/api/v1/me',
} as const

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const authService = {
  /**
   * Autentica o usuário e devolve o par access/refresh tokens.
   *
   * O backend retorna o MESMO 401 com mensagem genérica para qualquer falha
   * (email inexistente, senha errada, usuário inativo) — para não vazar
   * informação. O handling do erro fica na página de login.
   */
  async login(email: string, password: string): Promise<TokenResponse> {
    const { data } = await api.post<TokenResponse>(
      ENDPOINTS.login,
      { email, password },
      { _skipErrorToast: true },
    )
    return data
  },

  /**
   * Troca o `refresh_token` por um par novo (rotação obrigatória).
   *
   * Falhas (token revogado, expirado, usuário inativo) retornam 401 — nesse
   * caso o caller (useInitializeApp ou interceptor) deve limpar o store e
   * redirecionar para `/login`.
   *
   * Usa `axios` diretamente (sem a instância `api`) para evitar que o
   * response interceptor capture o 401 e dispare `redirectToLogin()` com
   * hard-reload, o que interferiria com o fluxo de bootstrap controlado
   * pelo `useInitializeApp`. O endpoint de refresh não precisa de headers
   * `Authorization` nem `X-Tenant-ID` — o refresh_token vai no body.
   */
  async refresh(refreshToken: string): Promise<TokenResponse> {
    const { data } = await axios.post<TokenResponse>(
      `${config.apiUrl}${ENDPOINTS.refresh}`,
      { refresh_token: refreshToken },
      {
        timeout: API_TIMEOUT_MS,
        headers: { 'Content-Type': 'application/json' },
      },
    )
    return data
  },

  /**
   * Revoga o `refresh_token` no servidor. Idempotente — chamar duas vezes
   * com o mesmo token resulta em 204 nas duas vezes.
   *
   * Retorna `void` (o backend responde 204 No Content).
   */
  async logout(refreshToken: string): Promise<void> {
    await api.post<void>(
      ENDPOINTS.logout,
      { refresh_token: refreshToken },
      { _skipErrorToast: true },
    )
  },

  /**
   * Retorna os dados do usuário autenticado (incluindo `accessible_tenants`
   * para popular o TenantSwitcher).
   *
   * Requer Bearer token — injetado automaticamente pelo request interceptor.
   */
  async getMe(): Promise<MeResponse> {
    const { data } = await api.get<MeResponse>(ENDPOINTS.me)
    return data
  },
}

export type AuthService = typeof authService
